import React, { forwardRef, useCallback } from 'react';
import Proptypes from 'prop-types';
import clsx from 'clsx';

import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '../../lib/styles';
import Toggle from '../Toggle';

const useStyles = makeStyles(theme => {
  return {
    root: {
      marginTop: 8,
      marginBottom: 4,
      width: '100%',
    },
    label: {
      marginTop: 9,
      justifyContent: 'space-between',
      marginLeft: 0,
      marginRight: 0,
      color: theme.palette.text.secondary,
      pointerEvents: 'none',
    },
    toggle: {
      pointerEvents: 'auto',
    },
  };
});

const ToggleInput = forwardRef(function ToggleInput(
  {
    checked,
    className,
    disabled,
    error,
    errorText,
    fullWidth,
    helpText,
    id,
    label,
    name,
    onChange,
    required,
    variant,
    inputRef,
    ...restProps
  },
  ref
) {
  const handleOnChange = useCallback((val, event) => onChange(val, event), [onChange]);
  const classes = useStyles();

  const control = (
    <Toggle
      {...restProps}
      checked={checked}
      className={classes.toggle}
      onChange={handleOnChange}
      inputProps={{ id, name }}
      variant={variant}
      inputRef={inputRef}
    />
  );

  return (
    <FormControl
      fullWidth={fullWidth}
      className={clsx([className, classes.root])}
      component="fieldset"
      ref={ref}
      error={error}
      disabled={disabled}
      required={required}
    >
      <FormControlLabel
        className={classes.label}
        control={control}
        label={label}
        labelPlacement="start"
        htmlFor={id}
      />
      {Boolean(helpText) && <FormHelperText>{helpText}</FormHelperText>}
      {error && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
});

ToggleInput.propTypes = {
  checked: Proptypes.bool,
  className: Proptypes.string,
  disabled: Proptypes.bool,
  error: Proptypes.bool,
  errorText: Proptypes.string,
  fullWidth: Proptypes.bool,
  helpText: Proptypes.string,
  id: Proptypes.string.isRequired,
  label: Proptypes.string.isRequired,
  name: Proptypes.string.isRequired,
  onChange: Proptypes.func.isRequired,
  required: Proptypes.bool,
  variant: Proptypes.oneOf(['default', 'redgreen']),
  inputRef: Proptypes.oneOfType([Proptypes.func, Proptypes.object]),
};

ToggleInput.defaultProps = {
  checked: false,
  className: '',
  disabled: false,
  error: false,
  errorText: '',
  fullWidth: true,
  helpText: '',
  required: false,
  variant: 'default',
  inputRef: null,
};

ToggleInput.displayName = 'ToggleInput';

export default ToggleInput;
